import { ReactElement } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";

import styles from "./FlowsheetFooter.module.scss";

/**
 * The properties for a {@link FlowsheetFooter} component.
 */
interface IFlowsheetFooterProps {
    /**
     * The event when the save button is clicked
     * @returns void
     */
    onSaveClicked: () => void;

    /**
     * The event when the cancel button is clicked
     * @returns void
     */
    onCancelClicked: () => void;

    /**
     * The event when the delete button is clicked
     * @returns void
     */
    onDeleteClicked: () => void;

    /**
     * Decides if we show the delete button
     */
    showDeleteButton: boolean;

    /**
     * Decides if we show the save button
     */
    showSaveButton: boolean;

    /*
     * Decides if we disable the save button
     */
    disableSaveButton: boolean;
}

/**
 * A component which serves as a footer for the flowsheet component.
 * @param props The component properties.
 */
function FlowsheetFooter({ onSaveClicked, onCancelClicked, onDeleteClicked, showDeleteButton, showSaveButton, disableSaveButton = false }: Readonly<IFlowsheetFooterProps>): ReactElement {
    const { getText } = useTranslation();

    return (
        <footer className={`${styles.flowsheetFooterContainer} ${!showDeleteButton ? styles.flexEnd : ""}`}>
            {showDeleteButton && (
                <ButtonControl
                    variant="error"
                    dataCy="flowsheet-footer-delete-button"
                    label={getText("DeleteLabel")}
                    onClick={() => onDeleteClicked()}
                />
            )}
            <ButtonGroup spacing="sm">
                <ButtonControl
                    variant="neutral"
                    onClick={() => onCancelClicked()}
                    label={getText("CancelLabel")}
                />
                {showSaveButton && (
                    <ButtonControl
                        variant="brand"
                        dataCy="flowsheet-footer-save-button"
                        onClick={() => onSaveClicked()}
                        label={getText("SaveLabel")}
                        disabled={disableSaveButton}
                    />
                )}
            </ButtonGroup>
        </footer>
    );
}

export { FlowsheetFooter };
